// src/components/IncidentView.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import { printDocument } from "../services/printDoc";
import {
  deleteCrime,
  fetchCrimeByNumber,
  fetchIncidenByIncidentId,
  updateCrimeStatus,
} from "../features/crimeSlice";
import DynamicBreadcrumbs from "../components/layout/Breadcrumb";
import Layout from "../components/layout/Layout";

const IncidentView = () => {
  const { incidentId } = useParams(); // incident _id
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Corrected version
  const { incidentRecords, crimeRecords, status } = useSelector(
    (state) => state.crime
  );

  // Find the specific incident by incidentId (string comparison)
  const incident =
    crimeRecords.find((rec) => rec.incidentId === incidentId) ||
    incidentRecords;

  // Local state for status update (initialize with existing value if found)
  const [crimeStatus, setCrimeStatus] = useState(
    incident?.incidentStatus || "pending"
  );

  // Fetch incident only when needed (on mount or when incidentId changes)
  // Avoid unnecessary fetches if data already exists in store
  useEffect(() => {
    // Only fetch if we don't already have this incident in the store
    if (!crimeRecords && incidentId) {
      dispatch(fetchIncidenByIncidentId(incidentId))
        .unwrap()
        .catch((err) => {
          console.error("Failed to fetch incident:", err);
          // Optional: show toast/notification
        });
    }
  }, [incidentId]); // Dependencies: re-run if incidentId or local incident changes

  // Optional: Update local status when fetched incident arrives
  useEffect(() => {
    if (incident?.incidentStatus) {
      setCrimeStatus(incident.incidentStatus);
    }
  }, [incident]);

  if (!incident) {
    return (
      <Layout title="Incident Not Found">
        <Container sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h5" color="error">
            Incident record not found.
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            onClick={() => navigate("/dashboard/view-crimes")}
          >
            Back to List
          </Button>
        </Container>
      </Layout>
    );
  }

  const handleStatusChange = (e) => {
    setCrimeStatus(e.target.value);
  };

  const handleUpdateStatus = () => {
    dispatch(updateCrimeStatus({ incidentId, crimeStatus }));
  };

  const handleDelete = async () => {
    if (
      window.confirm("Are you sure you want to delete this incident record?")
    ) {
      const result = await dispatch(deleteCrime(incidentId));
      if (deleteCrime.fulfilled.match(result)) {
        navigate("/dashboard/view-crimes");
      }
    }
  };

  const statusColors = {
    pending: "default",
    inCourt: "error",
    trial: "warning",
    atPoliceStation: "info",
    clear: "success",
  };

  return (
    <Layout
      title="Incident Details"
      description="Uttar Pradesh Police Crime Records"
    >
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <DynamicBreadcrumbs />

        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, color: "primary.dark" }}
        >
          Incident Details - {incident.incidentId}
        </Typography>

        <Paper
          elevation={4}
          sx={{ p: 4, borderRadius: 3, bgcolor: "background.paper" }}
        >
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Crime Type
              </Typography>
              <Typography variant="h6">{incident.crimeType}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="h6">
                {new Date(incident.crimeDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1">
                {incident.district}, {incident.tehsil}, {incident.villageTown}{" "}
                <br />
                Police Station: <strong>{incident.policeStation}</strong>
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Current Status
              </Typography>
              <Chip
                label={incident.incidentStatus}
                color={statusColors[incident.incidentStatus]}
                size="medium"
                sx={{ fontWeight: 600, mt: 1 }}
              />
            </Grid>

            {/* Offenders */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Offenders Involved
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {incident.offenders?.length > 0 ? (
                <Grid container spacing={2}>
                  {incident.offenders.map((off, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {off.personId?.firstName} {off.personId?.middleName}{" "}
                            {off.personId?.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Crime Number: <strong>{off.crimeNumber}</strong>
                          </Typography>
                          <Typography variant="body2">
                            Role: {off.role || "Offender"}
                          </Typography>

                          <Typography variant="body2">
                            Status:{" "}
                            <Chip
                              label={incident.incidentStatus}
                              color={statusColors[incident.incidentStatus]}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary">
                  No offenders listed
                </Typography>
              )}
            </Grid>

            {/* Victims */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Victims
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {incident.victims?.length > 0 ? (
                <List dense>
                  {incident.victims.map((v, i) => (
                    <ListItem key={i}>
                      <ListItemText
                        primary={v.name}
                        secondary={`Aadhaar: ${v.aadhar || "N/A"} • Mobile: ${
                          v.mobile || "N/A"
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  No victims recorded
                </Typography>
              )}
            </Grid>

            {/* Witnesses */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Eye Witnesses
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {incident.eyeWitnesses?.length > 0 ? (
                <List dense>
                  {incident.eyeWitnesses.map((w, i) => (
                    <ListItem key={i}>
                      <ListItemText
                        primary={w.name}
                        secondary={`Mobile: ${w.mobile || "N/A"}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  No witnesses recorded
                </Typography>
              )}
            </Grid>

            {/* Description & Reason */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mt: 1 }}
              >
                {incident.description || "No description provided"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Reason / Motive
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mt: 1 }}
              >
                {incident.reason || "Not specified"}
              </Typography>
            </Grid>

            {/* Registered By */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Registered By
              </Typography>
              <Typography variant="body1">
                {incident.registeredBy?.firstName}{" "}
                {incident.registeredBy?.lastName}
              </Typography>
            </Grid>

            {/* Actions */}
            <Grid
              item
              xs={12}
              sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateStatus}
                disabled={status === "updateStatus"}
                sx={{ minWidth: 160 }}
              >
                {status === "updateStatus" ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Update Status"
                )}
              </Button>

              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Change Status</InputLabel>
                <Select
                  value={crimeStatus}
                  label="Change Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="inCourt">In Court</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="atPoliceStation">At Police Station</MenuItem>
                  <MenuItem value="clear">Clear</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={status === "deleteStatus"}
                sx={{ minWidth: 160 }}
              >
                {status === "deleteStatus" ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Delete Incident"
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={() => printDocument()}
                sx={{ minWidth: 160 }}
              >
                Print Record
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Layout>
  );
};

export default IncidentView;
